// Pure view-state helpers for TaskMetadata display rules across execution, plan, and dashboard surfaces.
import { formatOverdueCaption } from '@/features/tasks/utils/views/taskTimeUtils';
import { resolveUnscheduledRailVisibleProjects } from '@/features/tasks/components/atoms/unscheduledRailProjectPills';
import { getPartiallyScheduledProgress } from '@/features/tasks/utils/views/filtersViewUtils';

export { resolveUnscheduledRailVisibleProjects } from '@/features/tasks/components/atoms/unscheduledRailProjectPills';
export { getPartiallyScheduledProgress } from '@/features/tasks/utils/views/filtersViewUtils';

/**
 * True for a "hoofdtaak": a top-level task that has at least one subtask (open or
 * closed). A childless top-level task is just an ordinary standalone task, not a
 * project container — its `date` means "work day", not "deadline".
 * @param {import('@/types').Task} task
 * @param {Map<string, import('@/types').Task>} tasksMap
 */
export const isMainTask = (task, tasksMap) => {
  if (!task || task.parentId || !tasksMap?.size) return false;
  for (const candidate of tasksMap.values()) {
    if (candidate.id !== task.id && candidate.parentId === task.id) {
      return true;
    }
  }
  return false;
};

/** @param {string[]} allProjects */
export function getVisibleProjects(allProjects, isExecutionView) {
  return isExecutionView ? (allProjects || []).slice(0, 1) : allProjects;
}

export function getHideDateChrome(
  isExecutionView,
  isPlanView,
  isMasterView,
  isDashboardView,
) {
  return isExecutionView || isPlanView || isMasterView || isDashboardView;
}

export function getShowDateOnDashboard(
  isDashboardView,
  isDashboardSurface,
  taskDate,
  isCompleted,
) {
  return (
    (isDashboardView || isDashboardSurface) && Boolean(taskDate) && !isCompleted
  );
}

export function getShowDateOnTodayUpcoming(
  isTodayUpcomingSurface,
  taskDate,
  isCompleted,
) {
  return (
    isTodayUpcomingSurface && Boolean(taskDate) && !isCompleted
  );
}

export function getHideNormalDateChrome(
  hideDateChrome,
  showDateOnDashboard,
  showDateOnTodayUpcoming,
  isSubtaskView,
) {
  return (
    (hideDateChrome && !showDateOnDashboard && !showDateOnTodayUpcoming) ||
    isSubtaskView
  );
}

/**
 * @param {{
 *   isSubtaskView: boolean,
 *   isCompleted: boolean,
 *   isMasterView: boolean,
 *   isOrganizeView: boolean,
 *   isDashboardSurface: boolean,
 *   isPlanView: boolean,
 *   displayDuration: number,
 *   isFocused: boolean,
 *   focusStartTime: number|null,
 * }} params
 */
export function getShowDuration({
  isSubtaskView,
  isCompleted,
  isMasterView,
  isOrganizeView,
  isDashboardSurface,
  isPlanView,
  displayDuration,
  isFocused,
  focusStartTime,
}) {
  if (isSubtaskView && isCompleted) return false;
  if (isMasterView || isOrganizeView) return false;
  if (isDashboardSurface && isPlanView) return false;
  return displayDuration > 0 || Boolean(isFocused && focusStartTime);
}

export function getShowDurationIcon(
  hideDateChrome,
  isMasterView,
  isDashboardView,
) {
  return !hideDateChrome && !isMasterView && !isDashboardView;
}

/**
 * @param {{
 *   allProjects: string[],
 *   taskDate: string|null,
 *   formattedDate: string|null,
 *   isExecutionView: boolean,
 *   isPlanView: boolean,
 *   isMasterView: boolean,
 *   isDashboardView: boolean,
 *   isDashboardSurface: boolean,
 *   isTodayUpcomingSurface: boolean,
 *   isSubtaskView: boolean,
 *   isCompleted: boolean,
 *   isOrganizeView: boolean,
 *   displayDuration: number,
 *   isFocused: boolean,
 *   focusStartTime: number|null,
 *   listId?: string|null,
 *   task?: import('@/types').Task|null,
 *   tasksMap?: Map<string, import('@/types').Task>|null,
 * }} viewProps
 */
export function buildMetadataViewState(viewProps) {
  const hideDateChrome = getHideDateChrome(
    viewProps.isExecutionView,
    viewProps.isPlanView,
    viewProps.isMasterView,
    viewProps.isDashboardView,
  );
  const showDateOnDashboard = getShowDateOnDashboard(
    viewProps.isDashboardView,
    viewProps.isDashboardSurface,
    viewProps.taskDate,
    viewProps.isCompleted,
  );
  const showDateOnTodayUpcoming = getShowDateOnTodayUpcoming(
    viewProps.isTodayUpcomingSurface,
    viewProps.taskDate,
    viewProps.isCompleted,
  );
  const baseProjects = getVisibleProjects(
    viewProps.allProjects,
    viewProps.isExecutionView,
  );

  return {
    visibleProjects: resolveUnscheduledRailVisibleProjects(baseProjects, {
      listId: viewProps.listId,
      task: viewProps.task,
      tasksMap: viewProps.tasksMap,
    }),
    overdueCaption:
      viewProps.isExecutionView || viewProps.isPlanView
        ? formatOverdueCaption(viewProps.taskDate)
        : null,
    partiallyScheduledProgress:
      viewProps.listId === 'unscheduled'
        ? getPartiallyScheduledProgress(viewProps.task, viewProps.tasksMap)
        : null,
    showDateBeforePills: Boolean(
      viewProps.isSubtaskView && viewProps.taskDate && viewProps.formattedDate,
    ),
    hideNormalDateChrome: getHideNormalDateChrome(
      hideDateChrome,
      showDateOnDashboard,
      showDateOnTodayUpcoming,
      viewProps.isSubtaskView,
    ),
    denseChips: viewProps.isDashboardView,
    showDuration: getShowDuration(viewProps),
    showDurationIcon: getShowDurationIcon(
      hideDateChrome,
      viewProps.isMasterView,
      viewProps.isDashboardView,
    ),
  };
}
